import { create } from "zustand";
import { persist } from "zustand/middleware";

import type { ProjectDataInput } from "@/features/project-data/schema";
import type { ConfirmedCitation, FeasibilitySummary } from "@/api/schemas";

export interface FeasibilityValues {
  building_type: string;
  building_subtype?: string | null;
  context?: string | null;
  designation?: string | null;
  in_city_center: boolean;
  work_type: string;
  municipality_code: string;
}

export interface ProjectDraft {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  currentStep: 1 | 2 | 3;
  feasibility?: FeasibilityValues;
  // Trimmed Step-1 verdict so revisiting the step re-shows the last
  // analysis instead of a blank form (full payload stays out of
  // localStorage).
  feasibilityResult?: FeasibilitySummary;
  projectData?: Partial<ProjectDataInput>;
  // Architect's sign-off on the proposed legal backbone (Step 2). The
  // pinned citations travel with the generate request so the izjava's
  // law list carries the article-level basis the architect confirmed.
  // Older drafts may lack ``citations`` — those confirmations are
  // display-only and don't flow into the document.
  legalConfirmation?: {
    confirmedAt: string;
    groundedTotal: number;
    citations?: ConfirmedCitation[];
  };
}

interface ProjectStore {
  projects: ProjectDraft[];
  activeId: string | null;
  setActive: (id: string | null) => void;
  upsert: (draft: ProjectDraft) => void;
  remove: (id: string) => void;
  patchActive: (patch: Partial<ProjectDraft>) => void;
  newProject: (name?: string) => string;
  /** P1 — start a new project pre-filled from an existing one (the architect's
   *  "template": reuse the office/projektant/zahvat data, change a few details). */
  duplicateProject: (sourceId: string, name?: string) => string;
  loadExample: () => string;
}

const EXAMPLE: Omit<ProjectDraft, "id" | "createdAt" | "updatedAt"> = {
  name: "Primjer — Ičići, k.č. 706/1",
  currentStep: 2,
  feasibility: {
    building_type: "obiteljska_kuca",
    building_subtype: "samostojeca",
    context: "Obiteljska kuća, KIG 0.23, KISN 0.43, P+1, visina v=6.5m, V=8.5m",
    designation: "M1",
    in_city_center: false,
    work_type: "novogradnja",
    municipality_code: "opatija",
  },
  projectData: {
    project_number: "PP-2026-001",
    project_location: "Ičići",
    investitor_name: "Petar Petrović",
    investitor_address: "Frana Krste Frankopana 12, 51410 Opatija",
    investitor_oib: "12345678903",
    ured_name: "Studio ARH d.o.o.",
    ured_address: "Maršala Tita 100, 51410 Opatija",
    glavni_projektant_name: "Ana Anić",
    glavni_projektant_title: "ovl. arh.",
    glavni_projektant_license: "A 1234",
    direktor_name: "Marko Marković",
    katastarska_cestica: "706/1",
    katastarski_opcina: "Veprinac",
    cestica_povrsina_m2: 720,
    katnost: "Po+P+1",
    tlocrtna_projekcija_m2: 168.4,
    building_length_m: 14.2,
    building_width_m: 11.8,
    building_height_v_m: 6.5,
    building_height_V_m: 8.5,
    num_stambenih: 1,
    num_poslovnih: 0,
    num_parking: 2,
    zelena_povrsina_m2: 410,
  },
};

function newId() {
  return `prj_${Math.random().toString(36).slice(2, 10)}_${Date.now().toString(36)}`;
}

/**
 * Local-first project store. Drafts live entirely in ``localStorage``
 * via Zustand's ``persist`` middleware — no backend persistence yet
 * (that's a separate sprint). The header's "Moji projekti" dropdown
 * reads ``projects`` directly.
 */
export const useProjectStore = create<ProjectStore>()(
  persist(
    (set, get) => ({
      projects: [],
      activeId: null,
      setActive: (id) => set({ activeId: id }),
      upsert: (draft) =>
        set((state) => {
          const existing = state.projects.findIndex((p) => p.id === draft.id);
          const next = [...state.projects];
          if (existing >= 0) next[existing] = draft;
          else next.unshift(draft);
          return { projects: next };
        }),
      remove: (id) =>
        set((state) => ({
          projects: state.projects.filter((p) => p.id !== id),
          activeId: state.activeId === id ? null : state.activeId,
        })),
      patchActive: (patch) =>
        set((state) => {
          if (!state.activeId) return state;
          const idx = state.projects.findIndex((p) => p.id === state.activeId);
          if (idx < 0) return state;
          const updated: ProjectDraft = {
            ...state.projects[idx],
            ...patch,
            updatedAt: new Date().toISOString(),
          };
          const projects = [...state.projects];
          projects[idx] = updated;
          return { projects };
        }),
      newProject: (name) => {
        const id = newId();
        const now = new Date().toISOString();
        const draft: ProjectDraft = {
          id,
          name: name ?? `Novi projekt ${new Date().toLocaleDateString("hr-HR")}`,
          createdAt: now,
          updatedAt: now,
          currentStep: 1,
        };
        get().upsert(draft);
        set({ activeId: id });
        return id;
      },
      duplicateProject: (sourceId, name) => {
        const src = get().projects.find((p) => p.id === sourceId);
        if (!src) return get().newProject(name);
        const id = newId();
        const now = new Date().toISOString();
        const draft: ProjectDraft = {
          id,
          name: name ?? `${src.name} (kopija)`,
          createdAt: now,
          updatedAt: now,
          // Land on Podaci so the architect adjusts the few per-project details.
          currentStep: 2,
          // Reuse the zahvat definition verbatim.
          feasibility: src.feasibility ? { ...src.feasibility } : undefined,
          // Carry the reusable project data, but reset the per-project identity:
          // a fresh project needs its own number, and the date defaults to today
          // (the single most common edit on a template reuse).
          projectData: src.projectData
            ? {
                ...src.projectData,
                project_number: "",
                project_date: new Date().toISOString().slice(0, 10),
              }
            : undefined,
          // The feasibility verdict and the legal sign-off are tied to the old
          // inputs — intentionally dropped so they're re-done for the new project.
        };
        get().upsert(draft);
        set({ activeId: id });
        return id;
      },
      loadExample: () => {
        const id = newId();
        const now = new Date().toISOString();
        const draft: ProjectDraft = {
          id,
          createdAt: now,
          updatedAt: now,
          ...EXAMPLE,
        };
        get().upsert(draft);
        set({ activeId: id });
        return id;
      },
    }),
    { name: "permitpilot.projects.v1" },
  ),
);
